import type { OverlayAxis, OverlayMetricId, OverlayPoint } from './types';

export type ChartLayout = {
  width: number;
  height: number;
  padL: number;
  padR: number;
  padT: number;
  padB: number;
};

export function scaleLinear(
  value: number,
  domain: [number, number],
  range: [number, number],
): number {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  if (d1 === d0) return (r0 + r1) / 2;
  return r0 + ((value - d0) / (d1 - d0)) * (r1 - r0);
}

export function domainFor(
  points: OverlayPoint[],
  ids: OverlayMetricId[],
  usePrior: boolean,
  axis: OverlayAxis,
  axisOf: (id: OverlayMetricId) => OverlayAxis,
): [number, number] {
  const vals: number[] = [];
  for (const p of points) {
    for (const id of ids) {
      if (axisOf(id) !== axis) continue;
      const v = p.values[id];
      if (v != null && Number.isFinite(v)) vals.push(v);
      if (usePrior) {
        const pv = p.priorValues[id];
        if (pv != null && Number.isFinite(pv)) vals.push(pv);
      }
    }
  }
  if (!vals.length) {
    if (axis === 'score') return [0, 100];
    return [0, 1];
  }
  let min = Math.min(...vals);
  let max = Math.max(...vals);
  if (min === max) {
    if (axis === 'score') return [0, 100];
    min = min * 0.9;
    max = max * 1.1 || 1;
  }
  if (axis === 'score') {
    return [0, 100];
  }
  if (min > 0) min = 0;
  const pad = (max - min) * 0.08;
  return [min, max + pad];
}

export function polyline(
  points: OverlayPoint[],
  id: OverlayMetricId,
  layout: ChartLayout,
  xCount: number,
  yDomain: [number, number],
  prior: boolean,
): string {
  const { width, height, padL, padR, padT, padB } = layout;
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;
  const parts: string[] = [];
  points.forEach((p, i) => {
    const raw = prior ? p.priorValues[id] : p.values[id];
    if (raw == null || !Number.isFinite(raw)) return;
    const x =
      xCount <= 1
        ? padL + innerW / 2
        : padL + (i / (xCount - 1)) * innerW;
    const y = padT + innerH - scaleLinear(raw, yDomain, [0, innerH]);
    parts.push(`${x.toFixed(1)},${y.toFixed(1)}`);
  });
  return parts.join(' ');
}

export function nearestIndex(localX: number, layout: ChartLayout, n: number): number {
  if (n <= 1) return 0;
  const { padL, padR, width } = layout;
  const innerW = width - padL - padR;
  const t = Math.max(0, Math.min(1, (localX - padL) / innerW));
  return Math.round(t * (n - 1));
}
